import { useState, useEffect, useCallback } from 'react';

// Hook para manejar notificaciones push del navegador
export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Verificar si el navegador soporta notificaciones
    const supported = 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.warn('Este navegador no soporta notificaciones');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error al solicitar permiso de notificaciones:', error);
      return false;
    }
  }, [isSupported]);

  const sendNotification = useCallback((title, options = {}) => {
    if (!isSupported || permission !== 'granted') {
      console.warn('No se pueden enviar notificaciones:', { isSupported, permission });
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: options.tag || 'default',
        renotify: true,
        requireInteraction: options.requireInteraction || false,
        ...options
      });

      // Auto-cerrar después de 10 segundos si no requiere interacción
      if (!options.requireInteraction) {
        setTimeout(() => notification.close(), 10000);
      }

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };

      return notification;
    } catch (error) {
      console.error('Error al enviar notificación:', error);
      return null;
    }
  }, [isSupported, permission]);

  // Notificación específica para nuevos leads
  const notifyNewLead = useCallback((leadData) => {
    const { senderName, platform, message, businessName } = leadData;
    
    const platformLabels = {
      'facebook_messenger': 'Facebook Messenger',
      'facebook_comment': 'Comentario Facebook',
      'instagram_dm': 'Instagram DM',
      'instagram_comment': 'Comentario Instagram',
      'whatsapp': 'WhatsApp'
    };

    const platformLabel = platformLabels[platform] || platform;
    const truncatedMessage = message?.length > 100 
      ? message.substring(0, 100) + '...' 
      : message;

    return sendNotification(`Nuevo Lead - ${businessName || 'HEBEH'}`, {
      body: `${senderName || 'Usuario'} vía ${platformLabel}\n"${truncatedMessage}"`,
      tag: 'new-lead',
      requireInteraction: true,
      onClick: () => {
        // Navegar a la pestaña de leads cuando se hace clic
        window.location.hash = '#leads';
      }
    });
  }, [sendNotification]);

  return {
    permission,
    isSupported,
    requestPermission,
    sendNotification,
    notifyNewLead
  };
}

export default usePushNotifications;
